<template>
  <b-container fluid class="d-flex flex-column py-3">
    <b-row class="mb-3">
      <b-col>
        <b-form-select
          v-model="item.readId"
          ref="selectReadId"
          :options="rules"
          @change="onChangeReadId"
        ></b-form-select>
      </b-col>
    </b-row>
    <b-table
      v-if="visibleList"
      hover
      no-border-collapse
      selectable
      select-mode="single"
      sticky-header
      thead-tr-class="small"
      tbody-tr-class="h5"
      :items="items"
      :fields="fields"
      @row-selected="onRowSelected"
    >
      <!-- 予定/実績 -->
      <template #cell(orderResult)="data">
        {{ data.item.orderCount }}&nbsp;/&nbsp;{{ data.item.resultCount }}
      </template>
      <!-- 照合 -->
      <template #cell(completeSts)="data">
        <span v-if="data.value === '2'" class="text-danger font-weight-bold">NG</span>
        <span v-else-if="data.item.shortFlg === 1" class="font-weight-bold">欠</span>
        <span v-else-if="data.value === '0'">未</span>
        <span v-else-if="data.value === '1'">中</span>
        <span v-else-if="data.value === '3'" class="text-primary font-weight-bold">OK</span>
        <span v-else-if="data.value === '4'" class="text-success font-weight-bold">RD</span>
      </template>
      <!-- 照合日時 -->
      <template #cell(compareDt)="data">
        <p class="small mb-0">
          {{ data.value ? data.value.split(' ')[0].substring(5).replace('-', '/') : '' }}
        </p>
        <p class="small mb-0">
          {{ data.value ? data.value.split(' ')[1].substring(0, 5) : '' }}
        </p>
      </template>
      <!-- 詳細へ -->
      <template #cell(toDetail)>
        <i class="bi bi-chevron-right"></i>
      </template>
    </b-table>
    <b-row v-if="visibleList">
      <b-col v-if="usableOutput">
        <b-button
          block
          variant="primary"
          class="rounded-pill"
          :disabled="disableButton"
          @click="onClickExport"
          >ファイル出力</b-button
        >
      </b-col>
      <b-col>
        <b-button
          block
          variant="danger"
          class="rounded-pill"
          :disabled="disableButton"
          @click="onClickDelete"
          >履歴削除</b-button
        >
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { getCompareResultListAPI, deleteCompareResultsAPI, postExportHistoryAPI } from '@/api';
import { createReadSelectItems, getCompareHeaderFieldsList, convertItemFormat } from '@/compare';
import { flg } from '@/constants';

export default {
  name: 'CompareHistory',
  data() {
    return {
      rules: [], // 読取ルールリスト
      fields: [], // 項目
      items: [], // 値
      item: { readId: null } // 照合画面情報
    };
  },
  async mounted() {
    // 画面を初期化
    this.$emit('initView', { viewId: this.$options.name, useToolButtons: false });

    // 読取ルールリスト作成
    this.rules = createReadSelectItems(this.$store.getters.reads);

    // 前画面からのパラメータをセット
    const read = this.$store.getters.currentRead;

    // 読取IDをパラメータ取得した場合、照合実績情報リストを取得する
    if (read) {
      this.item.readId = read.readId;

      // 照合実績を検索する
      await this.getCompareResultList(this.item.readId, this.$store.getters.currentCompare);
    }
  },
  computed: {
    /**
     * リスト表示状態
     */
    visibleList: function () {
      return this.item.readId;
    },
    usableOutput: function () {
      return this.$store.getters.term.csvOutputFlg === flg.ON;
    },
    /**
     * ボタン使用不可状態
     */
    disableButton: function () {
      return !this.item.readId && this.items.length === 0;
    }
  },
  methods: {
    /**
     * 照合対象変更時
     * @param {string} event イベント引数(読取ID)
     */
    onChangeReadId: async function (event) {
      this.$store.commit('setMessage', null);

      // 読取IDを保持
      this.$store.commit('setReadId', event);

      // 画面情報の属性をセット
      this.fields.splice(0);
      this.items.splice(0);
      if (!this.$store.getters.currentCompare) return;

      // 照合実績情報リスト検索
      await this.getCompareResultList(event, this.$store.getters.currentCompare);

      // 読取ルールのフォーカスを外す
      this.$refs.selectReadId?.blur();
    },
    /**
     * 照合実績行選択時
     * @param {object} selectedItems 選択項目(照合実績)
     */
    onRowSelected: async function (selectedItems) {
      // 次画面へのパラメータセット
      this.$store.commit('incrementHistoryCount');

      // 照合履歴詳細画面へ
      const url = `/CompareHistoryDetail/${selectedItems[0].compareOrderNo}/${selectedItems[0].compareResultNo}`;
      this.$router.push({ path: url });
    },
    /**
     * 履歴出力ボタン
     */
    onClickExport: async function () {
      // 出力履歴情報登録API
      const read = this.$store.getters.currentRead;
      const history = { readId: read.readId, compareId: read.compareId };
      const ret = await postExportHistoryAPI(history);
      if (!ret) {
        this.$store.commit('setMessage', '通信エラー(照合履歴CSV出力)');
        return;
      }

      this.$store.commit('setMessage', '履歴情報CSVを出力しました');
    },
    /**
     * 履歴削除ボタン
     */
    onClickDelete: async function () {
      this.$store.commit('setMessage', null);

      if (!confirm('履歴を削除します')) return;

      // 照合履歴情報削除API
      const ret = await deleteCompareResultsAPI(this.item.readId);
      if (!ret) {
        this.$store.commit('setMessage', '通信エラー(履歴情報削除)');
        return;
      }

      // 残データを再表示する
      this.items.splice(0);
      this.items = this.items.concat(ret.data);
      for (let item of this.items) {
        convertItemFormat(this.$store.getters.currentCompare, item);
      }

      this.$store.commit('setMessage', '履歴情報を削除しました');
    },
    /**
     * 照合実績情報リスト取得
     * @param {string} readId 読取ID
     * @param {!object} compare 照合情報
     */
    getCompareResultList: async function (readId, compare) {
      // 一覧表示項目選択
      const fields = getCompareHeaderFieldsList(compare);
      fields.push({ key: 'completeSts', label: '照合' });
      this.fields = fields.map(function (x) {
        return { key: x.key, label: x.label, thClass: 'p-1', tdClass: 'px-1' };
      });
      this.fields.push({ key: 'compareDt', label: '日時', thClass: 'p-1', tdClass: 'p-1' });
      this.fields.push({
        key: 'toDetail',
        label: '',
        thClass: 'p-1',
        tdClass: 'text-right px-0'
      });

      // 値セット
      // 照合実績情報リストAPI
      const ret = await getCompareResultListAPI(readId);
      if (!ret) {
        this.items.splice(0);
        this.$store.commit('setMessage', '通信エラー(履歴情報リスト取得)');
        return;
      }

      this.items = ret.data;
      for (let item of this.items) {
        convertItemFormat(compare, item);
      }
    }
  }
};
</script>

<style>
div.container-fluid {
  height: 100%;
}
.b-table-sticky-header {
  max-height: none !important;
  -webkit-flex: 1 1 0;
  -ms-flex: 1 1 0;
  flex: 1 1 0;
}
</style>
